import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { BreadcrumbsTable } from "../../../components/common/HelperFunc";

const PARENT_TITLE = "기준정보";
const CHILD_TITLE = "위험성평가 주기 설정";

const CYCLE_OPTIONS = [
  { value: "monthly", label: "매월", months: 1 },
  { value: "quarterly", label: "분기", months: 3 },
  { value: "half_year", label: "반기", months: 6 },
  { value: "yearly", label: "연간", months: 12 },
  { value: "custom_month", label: "직접 입력", months: null },
];

const DEFAULT_FORM = {
  cycle_type: "monthly",
  cycle_months: 1,
  base_day: 1,
  remind_days_before: 7,
};

const pageContainerSx = {
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
  bgcolor: "var(--mui-palette-background-paper)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const contentSx = {
  flex: 1,
  minHeight: 0,
  p: { xs: "12px", lg: "16px 32px 16px 32px" },
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const tableShellSx = {
  flex: 1,
  minHeight: 0,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "0.375rem",
  bgcolor: "#fff",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
};

const tableBodySx = {
  height: "100%",
  minHeight: 0,
  overflow: "auto",
  p: { xs: 1.5, md: 2 },
};

const settingPanelSx = {
  height: { xs: "auto", lg: "100%" },
  minWidth: 0,
  minHeight: 420,
  border: "1px solid",
  borderColor: "#e5e7eb",
  borderRadius: "0.5rem",
  bgcolor: "#fff",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const panelHeaderSx = {
  minHeight: 66,
  px: { xs: 1.75, md: 2.25 },
  py: 1.5,
  borderBottom: "1px solid",
  borderColor: "#eef2f7",
  bgcolor: "#fbfcff",
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.5,
};

const fieldCardSx = {
  p: 1.5,
  border: "1px solid",
  borderColor: "#edf0f5",
  borderRadius: "0.5rem",
  bgcolor: "#fff",
  transition: "border-color 160ms ease, box-shadow 160ms ease",
  "&:focus-within": {
    borderColor: "#7c6ff6",
    boxShadow: "0 0 0 3px rgba(124, 111, 246, 0.12)",
  },
};

const iconBoxSx = (color, bg) => ({
  width: 40,
  height: 40,
  borderRadius: "0.45rem",
  bgcolor: bg,
  color,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const toInt = (value, fallback) => {
  const next = Number(value);
  return Number.isFinite(next) ? Math.trunc(next) : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const pad2 = (value) => String(value).padStart(2, "0");

const getCycleOption = (type) =>
  CYCLE_OPTIONS.find((option) => option.value === type) || CYCLE_OPTIONS[0];

const normalizeForm = (source = {}) => {
  const option = getCycleOption(source.cycle_type);
  const cycleMonths =
    option.months ||
    clamp(toInt(source.cycle_months, DEFAULT_FORM.cycle_months), 1, 60);

  return {
    cycle_type: option.value,
    cycle_months: cycleMonths,
    base_day: clamp(toInt(source.base_day, DEFAULT_FORM.base_day), 1, 28),
    remind_days_before: clamp(
      toInt(source.remind_days_before, DEFAULT_FORM.remind_days_before),
      0,
      60
    ),
  };
};

const buildPreviewDates = (form) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const cycleMonths = Math.max(1, Number(form.cycle_months || 1));
  const baseDay = clamp(Number(form.base_day || 1), 1, 28);
  const dates = [];

  for (let offset = 0; offset < 36 && dates.length < 6; offset += 1) {
    const monthIndex = currentMonth + offset;
    if (monthIndex % cycleMonths !== 0) continue;

    const date = new Date(currentYear, monthIndex, baseDay);
    const todayDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    if (date < todayDate) continue;

    dates.push(
      `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
        date.getDate()
      )}`
    );
  }

  return dates;
};

export function StandardEvalCycleSetting() {
  const { group_code, project_code } = useParams();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(DEFAULT_FORM);

  const apiBase = `/risk/${group_code}/${project_code}/eval-cycle`;
  const queryKey = ["risk-eval-cycle-setting", group_code, project_code];

  const settingQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await axiosInstance.get(apiBase);
      return response.data?.setting || DEFAULT_FORM;
    },
    enabled: Boolean(group_code && project_code),
  });

  useEffect(() => {
    if (settingQuery.data) {
      setForm(normalizeForm(settingQuery.data));
    }
  }, [settingQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await axiosInstance.put(apiBase, payload);
      return response.data?.setting;
    },
    onSuccess: (setting) => {
      queryClient.setQueryData(queryKey, setting);
      setForm(normalizeForm(setting));
      toast.success("위험성평가 주기 설정이 저장되었습니다.");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "위험성평가 주기 설정 저장 중 오류가 발생했습니다."
      );
    },
  });

  const previewDates = useMemo(() => buildPreviewDates(form), [form]);

  const handleCycleTypeChange = (event) => {
    const option = getCycleOption(event.target.value);
    setForm((prev) => ({
      ...prev,
      cycle_type: option.value,
      cycle_months: option.months || prev.cycle_months || 1,
    }));
  };

  const handleNumberChange = (key, min, max) => (event) => {
    const value = clamp(toInt(event.target.value, form[key]), min, max);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    saveMutation.mutate(normalizeForm(form));
  };

  return (
    <Box className="safe-page-root" sx={pageContainerSx}>
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />

      <Box className="safe-page-body" sx={contentSx}>
        <Paper elevation={0} sx={tableShellSx}>
          <Box sx={tableBodySx}>
            <Grid container spacing={2} sx={{ height: "100%" }}>
              <Grid item xs={12} lg={7.4}>
                <Box sx={settingPanelSx}>
                  <Box sx={panelHeaderSx}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box sx={iconBoxSx("#635bff", "#f0eeff")}>
                        <CalendarMonthIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
                            평가 주기 설정
                          </Typography>
                          {settingQuery.isFetching ? (
                            <CircularProgress size={15} />
                          ) : null}
                        </Stack>
                        <Typography
                          sx={{ mt: 0.35, color: "#667085", fontSize: 12.5 }}
                        >
                          기준일과 알림 시점을 설정합니다.
                        </Typography>
                      </Box>
                    </Stack>

                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={saveMutation.isPending}
                      sx={{
                        minWidth: 86,
                        fontWeight: 800,
                        borderRadius: "0.375rem",
                        boxShadow: "0 6px 14px rgba(105, 91, 230, 0.22)",
                        flexShrink: 0,
                      }}
                    >
                      저장
                    </Button>
                  </Box>

                  <Box sx={{ p: { xs: 1.75, md: 2.25 } }}>
                    <Grid container spacing={1.75}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={fieldCardSx}>
                          <FormControl fullWidth>
                            <InputLabel>평가 주기</InputLabel>
                            <Select
                              label="평가 주기"
                              value={form.cycle_type}
                              onChange={handleCycleTypeChange}
                              sx={{ bgcolor: "#fff" }}
                            >
                              {CYCLE_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={fieldCardSx}>
                          <TextField
                            fullWidth
                            label="개월 간격"
                            type="number"
                            value={form.cycle_months}
                            onChange={handleNumberChange("cycle_months", 1, 60)}
                            disabled={form.cycle_type !== "custom_month"}
                            inputProps={{ min: 1, max: 60 }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={fieldCardSx}>
                          <TextField
                            fullWidth
                            label="기준일"
                            type="number"
                            value={form.base_day}
                            onChange={handleNumberChange("base_day", 1, 28)}
                            inputProps={{ min: 1, max: 28 }}
                            helperText="1일부터 28일까지 사용할 수 있습니다."
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={fieldCardSx}>
                          <TextField
                            fullWidth
                            label="사전 알림"
                            type="number"
                            value={form.remind_days_before}
                            onChange={handleNumberChange(
                              "remind_days_before",
                              0,
                              60
                            )}
                            inputProps={{ min: 0, max: 60 }}
                            helperText="평가 예정일 며칠 전에 표시할지 설정합니다."
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} lg={4.6}>
                <Box sx={settingPanelSx}>
                  <Box sx={panelHeaderSx}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box sx={iconBoxSx("#0891b2", "#ecfeff")}>
                        <CalendarMonthIcon fontSize="small" />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
                          예정일 미리보기
                        </Typography>
                        <Typography
                          sx={{ mt: 0.35, color: "#667085", fontSize: 12.5 }}
                        >
                          현재 설정 기준 다음 평가일입니다.
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <Stack
                    spacing={1}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      p: { xs: 1.5, md: 1.75 },
                      scrollbarWidth: "thin",
                      scrollbarColor: "#cbd5e1 transparent",
                      "&::-webkit-scrollbar": { width: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#cbd5e1",
                        borderRadius: 8,
                      },
                    }}
                  >
                    {previewDates.map((date, index) => (
                      <Stack
                        key={date}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          minHeight: 56,
                          px: 1.4,
                          border: "1px solid",
                          borderColor: index === 0 ? "#7c6ff6" : "#e5e7eb",
                          borderRadius: "0.5rem",
                          bgcolor: index === 0 ? "#f5f3ff" : "#fff",
                        }}
                      >
                        <Stack spacing={0.2}>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: 16,
                              color: "#1f2937",
                            }}
                          >
                            {date}
                          </Typography>
                          {index === 0 ? (
                            <Typography sx={{ color: "#6b7280", fontSize: 12 }}>
                              다음 평가 예정일
                            </Typography>
                          ) : null}
                        </Stack>
                        <Chip
                          size="small"
                          label={index === 0 ? "다음 예정" : `${index + 1}번째`}
                          color={index === 0 ? "primary" : "default"}
                          variant={index === 0 ? "filled" : "outlined"}
                          sx={{ fontWeight: 800, borderRadius: "0.375rem" }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
